import * as React from "react";
import { motion } from "motion/react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "./tooltip";

export const Skeleton = ({ className }: { className?: string }) => <div className={cn("animate-pulse rounded-xl bg-muted", className)} />;

/** Kis „i” jel a szám mellé: egy mondatban elmondja, mit tartalmaz az összeg. */
export function InfoTip({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Tip content={children}>
      <button type="button" aria-label="Mit tartalmaz?" className={cn("inline-flex shrink-0 text-muted-foreground/70 transition hover:text-foreground", className)}>
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tip>
  );
}

export const Separator = ({ className }: { className?: string }) => <div className={cn("h-px w-full bg-border/70", className)} />;

/** Barátságos üres állapot, egy kis lebegő ikonnal. */
export function EmptyState({ icon, title, description, action, className }: { icon: React.ReactNode; title: string; description?: React.ReactNode; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-14 text-center", className)}>
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1, y: [0, -6, 0] }}
        transition={{ scale: { type: "spring", stiffness: 260, damping: 18 }, y: { duration: 3.2, repeat: Infinity, ease: "easeInOut" } }}
        className="mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-accent text-accent-foreground shadow-soft [&_svg]:size-9"
      >
        {icon}
      </motion.div>
      <h3 className="text-lg">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/** Oldalfejléc: cím, alcím, jobb oldali műveletek. Mobilon a műveletek a cím alá törnek. */
export function PageHeader({ title, subtitle, actions, eyebrow }: { title: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode; eyebrow?: React.ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 animate-fade-up">
        {eyebrow && <div className="mb-1 text-sm font-semibold text-primary-text">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1.5 text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/** Lista elemek lépcsőzetes beúszása. */
export const staggerList = {
  container: { hidden: {}, show: { transition: { staggerChildren: 0.05 } } },
  item: { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 380, damping: 30 } } },
};
